import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Check, ChevronRight, X, Shield, MessageCircle, TrendingUp, Sparkles } from "lucide-react";

const FIELD_LABEL = "text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono";

export function SetupWizard({ onComplete, onSkip }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState({
    dhan_client_id: "",
    access_token: "",
    broker_pin: "",
    totp_secret: "",
  });
  const [tg, setTg] = useState({ telegram_bot_token: "", telegram_chat_id: "", telegram_enabled: true });
  const [strat, setStrat] = useState({
    name: "NIFTY ATM CE Demo",
    symbol: "NIFTY",
    option_type: "CE",
    moneyness: "ATM",
    quantity: 1,
    entry_price: 100,
    sl_points: 2,
    target_gap: 10,
  });
  const [stepsDone, setStepsDone] = useState({ 1: false, 2: false, 3: false });

  const saveCreds = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/broker/config`, { ...creds, broker: "dhan", mode: "PAPER", auto_square_off: "15:15", max_loss: -5000, max_profit: 10000, kill_switch: false });
      const v = await axios.post(`${API}/broker/validate`).catch(() => ({ data: { valid: false } }));
      setStepsDone((s) => ({ ...s, 1: true }));
      if (v.data.valid) toast.success("Dhan credentials validated ✓");
      else toast.message("Saved. Token validation deferred — verify on dashboard.");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveTelegram = async () => {
    setBusy(true);
    try {
      const cur = (await axios.get(`${API}/broker/config`)).data;
      await axios.post(`${API}/broker/config`, { ...cur, ...tg });
      if (tg.telegram_bot_token && tg.telegram_chat_id) {
        const r = await axios.post(`${API}/telegram/test`).catch((e) => ({ data: { ok: false, err: e.message } }));
        if (r.data.ok) toast.success("Telegram alert delivered ✓");
        else toast.warning("Telegram saved but test failed. Check token/chat ID.");
      }
      setStepsDone((s) => ({ ...s, 2: true }));
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveStrategy = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/strategy`, {
        ...strat,
        exchange: "NSE",
        exchange_segment: "NSE_FNO",
        instrument_family: "Option",
        side: "BUY",
        strike_selection: "AUTO",
        expiry_type: "CURRENT",
        entry_type: "LIMIT",
        limit_price: strat.entry_price - 1,
        t1_qty_pct: 25,
        t2_qty_pct: 25,
        t3_qty_pct: 50,
        re_entry_on_sl: true,
        re_entry_on_target: false,
        product_type: "INTRADAY",
        status: "DRAFT",
      });
      setStepsDone((s) => ({ ...s, 3: true }));
      toast.success("Strategy saved! Ready to deploy.");
      setTimeout(onComplete, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="setup-wizard">
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-2xl my-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <div>
              <div className="text-base font-bold">Quick Setup</div>
              <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">3 steps · ~2 minutes</div>
            </div>
          </div>
          <Button onClick={onSkip} variant="ghost" size="sm" className="text-zinc-400" data-testid="wizard-skip"><X className="h-4 w-4 mr-1" /> Skip</Button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-4 flex items-center gap-2 border-b border-zinc-900">
          {[
            { n: 1, label: "Dhan", icon: Shield },
            { n: 2, label: "Telegram", icon: MessageCircle },
            { n: 3, label: "Strategy", icon: TrendingUp },
          ].map(({ n, label, icon: Icon }, idx, arr) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <button onClick={() => setStep(n)} className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-mono uppercase tracking-wider transition ${step === n ? "bg-amber-500 text-black" : stepsDone[n] ? "bg-emerald-950 text-emerald-400" : "bg-zinc-900 text-zinc-500"}`} data-testid={`wizard-step-${n}`}>
                {stepsDone[n] ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                <span className="hidden sm:inline">{label}</span>
              </button>
              {idx < arr.length - 1 && <div className={`flex-1 h-px ${stepsDone[n] ? "bg-emerald-700" : "bg-zinc-800"}`} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          {step === 1 && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-300">Enter your Dhan credentials. Get them at <a href="https://web.dhan.co" target="_blank" rel="noreferrer" className="text-amber-400 underline">web.dhan.co</a> → My Profile → Access DhanHQ APIs.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Dhan Client ID</Label><Input data-testid="wiz-client-id" value={creds.dhan_client_id} onChange={(e) => setCreds({ ...creds, dhan_client_id: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Broker PIN (6-digit)</Label><Input data-testid="wiz-pin" type="password" value={creds.broker_pin} onChange={(e) => setCreds({ ...creds, broker_pin: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label className={FIELD_LABEL}>Access Token (JWT)</Label><Input data-testid="wiz-token" type="password" value={creds.access_token} onChange={(e) => setCreds({ ...creds, access_token: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label className={FIELD_LABEL}>TOTP Secret (optional, enables auto-token)</Label><Input data-testid="wiz-totp" type="password" value={creds.totp_secret} onChange={(e) => setCreds({ ...creds, totp_secret: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">Token expires every 24h. Add TOTP secret to enable one-click auto-refresh.</div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-300">Get notified on every order, SL/target hit, daily P&L summary.</div>
              <div className="space-y-1.5"><Label className={FIELD_LABEL}>Bot Token</Label><Input data-testid="wiz-tg-token" type="password" placeholder="123456:ABC..." value={tg.telegram_bot_token} onChange={(e) => setTg({ ...tg, telegram_bot_token: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
              <div className="space-y-1.5"><Label className={FIELD_LABEL}>Chat ID</Label><Input data-testid="wiz-tg-chat" placeholder="123456789" value={tg.telegram_chat_id} onChange={(e) => setTg({ ...tg, telegram_chat_id: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
              <div className="text-[11px] text-zinc-500 font-mono leading-relaxed">
                <div>1. Telegram → search <strong>@BotFather</strong> → /newbot → save token</div>
                <div>2. Send message to your bot, then visit <code className="text-amber-400">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> to find chat_id</div>
                <div className="mt-1 text-zinc-600">Skip this step to set up later.</div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-300">Save your first strategy. You can deploy it in PAPER mode immediately to test.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Name</Label><Input data-testid="wiz-strat-name" value={strat.name} onChange={(e) => setStrat({ ...strat, name: e.target.value })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Symbol</Label>
                  <select className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-zinc-100" data-testid="wiz-strat-symbol" value={strat.symbol} onChange={(e) => setStrat({ ...strat, symbol: e.target.value })}>
                    {["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "SENSEX", "BANKEX", "CRUDEOIL", "CRUDEOILM", "NATURALGAS", "NATGASMINI", "NATURALGASM", "GOLD", "SILVER"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Option Type</Label>
                  <select className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-zinc-100" value={strat.option_type} onChange={(e) => setStrat({ ...strat, option_type: e.target.value })}>
                    <option value="CE">CE (Call)</option><option value="PE">PE (Put)</option>
                  </select>
                </div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Moneyness</Label>
                  <select className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-zinc-100" value={strat.moneyness} onChange={(e) => setStrat({ ...strat, moneyness: e.target.value })}>
                    {["ATM", "OTM1", "OTM2", "OTM3", "ITM1", "ITM2", "ITM3"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Quantity (lots)</Label><Input type="number" min="1" value={strat.quantity} onChange={(e) => setStrat({ ...strat, quantity: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Entry Price (₹)</Label><Input type="number" step="0.05" value={strat.entry_price} onChange={(e) => setStrat({ ...strat, entry_price: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>SL (Points)</Label><Input type="number" step="0.5" value={strat.sl_points} onChange={(e) => setStrat({ ...strat, sl_points: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
                <div className="space-y-1.5"><Label className={FIELD_LABEL}>Target Gap (₹)</Label><Input type="number" step="0.5" value={strat.target_gap} onChange={(e) => setStrat({ ...strat, target_gap: Number(e.target.value) })} className="bg-zinc-900 border-zinc-800 font-mono" /></div>
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">T1/T2/T3 split 25/25/50% with SL = Entry − {strat.sl_points} pts, T1 = Entry + {strat.target_gap} pts.</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 flex justify-between items-center bg-zinc-950/50">
          <Button onClick={() => setStep(Math.max(1, step - 1))} variant="ghost" disabled={step === 1} className="text-zinc-400">Back</Button>
          {step === 1 && (
            <Button onClick={saveCreds} disabled={busy || !creds.dhan_client_id || !creds.access_token} data-testid="wiz-next-1" className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
              {busy ? "Saving…" : "Continue"} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <div className="flex gap-2">
              <Button onClick={() => { setStepsDone((s) => ({ ...s, 2: true })); setStep(3); }} variant="outline" className="border-zinc-700">Skip</Button>
              <Button onClick={saveTelegram} disabled={busy} data-testid="wiz-next-2" className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
                {busy ? "Saving…" : "Continue"} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
          {step === 3 && (
            <Button onClick={saveStrategy} disabled={busy || !strat.name} data-testid="wiz-finish" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
              {busy ? "Saving…" : "Finish Setup"} <Check className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
